<script setup lang="ts">
type Props = {
  id?: string;
  initialValue?: string | number;
  disabled?: boolean;
  size?: string;
  label?: string;
  max?: number;
  min?: number;
};

const {
  initialValue = '',
  disabled = false,
  size = 'md',
  label = '',
  max = 999999999,
  min = 0,
} = defineProps<Props>();

const modelValue = defineModel<string | number>('modelValue', {
  default: '',
});

const onInput = (event: Event) => {
  const input = event.target as HTMLInputElement;
  let value = input.value;

  value = value.replace(/\D/g, '');

  const numericValue = parseInt(value) || 0;

  if (numericValue < min) {
    modelValue.value = min.toString();
  } else if (numericValue > max) {
    modelValue.value = max.toString();
  } else {
    modelValue.value = value;
  }
};

const incrementValue = () => {
  if (!disabled) {
    const newValue = parseInt(String(modelValue.value)) + 1;

    if (newValue <= max) {
      modelValue.value = newValue.toString();
    }
  }
};

const decrementValue = () => {
  if (!disabled) {
    const newValue = parseInt(String(modelValue.value)) - 1;

    if (newValue >= min) {
      modelValue.value = newValue.toString();
    }
  }
};

watchEffect(() => (modelValue.value = initialValue));
</script>

<template>
  <div :class="$style.inputNumberContainer">
    <div :class="$style.inputContainer">
      <button
        :class="[
          $style.buttonValue,
          $style.minusButton,
          { [$style.lg]: size === 'lg' },
          { [$style.withLabel]: !!label },
        ]"
        @click="decrementValue"
      >
        -
      </button>
      <input
        v-model="modelValue"
        :class="[
          $style.inputNumber,
          { [$style.lg]: size === 'lg' },
          { [$style.withLabel]: !!label },
        ]"
        density="compact"
        :disabled="disabled"
        inputmode="decimal"
        :max="max"
        :min="min"
        placeholder="Valor"
        step="0"
        type="number"
        variant="solo"
        @input="onInput"
      />
      <button
        :class="[
          $style.buttonValue,
          $style.plusButton,
          { [$style.lg]: size === 'lg' },
          { [$style.withLabel]: !!label },
        ]"
        @click="incrementValue"
      >
        +
      </button>
    </div>

    <label
      v-if="label"
      :class="$style.label"
      :for="id"
    >
      {{ label }}
    </label>
  </div>
</template>

<style module lang="postcss">
.inputNumberContainer {
  @apply flex flex-col;

  .label {
    @apply text-2xs leading-3 font-medium h-4 text-auth-texts/60 bg-auth-bg-inputs px-2 py-0.5 rounded-b-lg flex items-center justify-center border-texts/10 border-solid border-0 border-t capitalize;
  }

  .inputContainer {
    @apply w-full relative flex justify-between items-center;

    .inputNumber {
      @apply h-8 px-4 w-full text-sm text-center border-none placeholder-auth-text-inputs/60 bg-auth-bg-inputs text-auth-text-inputs focus:border-transparent focus:outline-none focus:ring-0;

      &.lg:not(.withLabel) {
        @apply h-12;
      }

      &.withLabel {
        @apply h-8 text-xs;
      }

      &[disabled]:not([disabled='false']) {
        @apply opacity-40 cursor-not-allowed;
      }
    }

    .buttonValue {
      @apply w-20 h-8 flex items-center justify-center bg-auth-bg-inputs text-auth-text-inputs/60 border-texts/10 border-solid border-0 text-lg font-bold;

      &.lg:not(.withLabel) {
        @apply h-12;
      }

      &.withLabel {
        @apply h-8;
      }

      &.minusButton {
        @apply rounded-l-lg border-r;

        &.withLabel {
          @apply rounded-bl-none;
        }
      }

      &.plusButton {
        @apply rounded-r-lg border-l;

        &.withLabel {
          @apply rounded-br-none;
        }
      }
    }
  }
}
</style>
